import { getConfig } from '@superbet-group/web.lib.config';
import {
    Country,
    IdentityProvider,
} from '@superbet-group/web.types';

import {
    createCTRestClient,
    CTResponseData,
    processResponse,
    RestClient,
} from '@superbet-group/web.lib.rest';
import { getClientSourceParam } from '@superbet-group/web.lib.vue-utilities';

import {
    PlayerLoginNotificationResponse,
    AccountRecoveryToken,
    LoginPlayerAlternativeChannelResponse,
} from '@accountFederation/modules/accountRecovery/types';
import { defaultHttpTimeoutMs } from '@core/constants';
import {
    LoginCredentials,
    LoginCredentialsWithSeonSession,
} from '@core/models/authentication/LoginCredentials';
import { antiFraudService } from '@core/services/antiFraud/AntiFraudService';
import { getCsrfToken } from '@core/services/authentication/helpers';
import RegisterInput from '@countryModels/authentication/RegisterInput';
import { getBlackboxPayload } from '@modules/iovation';
import { shellLocalConfig } from '@src/config/shell/localConfig';

import {
    HrPlayerDataDTO,
    IdentityProviderUrlResponse,
    LoginResponse,
    RegisterResponse,
    LoginHeaders,
    RegisterHeaders,
    VerifyMfaCodePayload,
    SendMfaCodePayload,
    VerifyMfaCodeResponse,
    SendMfaCodeResponse,
    SessionStatsResponse,
} from './Types';
import { wafService } from '@core/services/waf/WafService';
import {
    AntifraudErrorLocation,
    logAntifraudError,
} from '@core/services/antiFraud/debug';
import { getDeviceId } from '@src/config/idGenerationService';
import { getMultiLoginQuery } from '@app/multiLogin/helpers';
import { getRegisterUrl } from './helpers';

export default class RestService {
    private validFieldCancelSource: any = {};
    private readonly endpoints = getConfig().services.playerAccountManagement;
    private readonly restClient: RestClient = createCTRestClient(
        getConfig().auth.host,
        defaultHttpTimeoutMs,
        getClientSourceParam(),
        true,
        getCsrfToken,
    );

    public async register(registerInput: RegisterInput): Promise<RegisterResponse> {
        // eslint-disable-next-line no-param-reassign
        registerInput.repeatpassword = registerInput.password;

        let seonPayload = {};
        if (shellLocalConfig.seonConfig.register.isServiceEnabled) {
            const { session, sessionId } = antiFraudService.getCurrentSession();

            if (!session || !sessionId) {
                logAntifraudError(AntifraudErrorLocation.RegisterGetSession, 'missing_session');
            }

            if (antiFraudService.isComtradeSeonFlow()) {
                seonPayload = {
                    deviceFingerprint: session,
                    externalDeviceId: getDeviceId(),
                };
            } else {
                seonPayload = {
                    session,
                    sessionId,
                    deviceFingerprint: getBlackboxPayload(),
                };
            }
        }

        const wafToken = await wafService.maybeGetToken();

        const headers: RegisterHeaders = {
            // eslint-disable-next-line sonarjs/no-duplicate-string
            'Content-Type': 'application/json',
            ...(wafToken ? { 'X-Aws-Waf-Token': wafToken } : {}),
        };

        return this.restClient.post(
            `${this.endpoints.apiVersion}/${getRegisterUrl()}`,
            {
                ...registerInput,
                ...seonPayload,
            },
            {
                headers,
            },
        );
    }

    public verifyPlayerDetails(playerData: HrPlayerDataDTO): Promise<any> {
        return this.restClient.post(
            '/api/v1/verifyPlayerDetails',
            playerData,
            {
                headers: {
                    'Content-Type': 'application/json',
                },
            },
        );
    }

    public isRegisterFieldValid(key: string, value: unknown): Promise<any> {
        if (this.validFieldCancelSource[key] && this.validFieldCancelSource[key] !== null) {
            this.validFieldCancelSource[key].abort();
            this.validFieldCancelSource[key] = null;
        }

        this.validFieldCancelSource[key] = new AbortController();

        return this.restClient.post(
            getConfig().services.playerAccountManagement.validateRegistrationData,
            {
                regData: {
                    [key]: value,
                },
            }, {
                signal: this.validFieldCancelSource[key].signal,
                headers: {
                    'Content-Type': 'application/json',
                },
            },
        );
    }

    public validateRegistrationData(regData: Record<string, any>): Promise<any> {
        return this.restClient.post(
            this.endpoints.validateRegistrationData,
            {
                regData,
            },
            {
                headers: {
                    'Content-Type': 'application/json',
                },
            },
        );
    }

    public async login(loginCredentials: LoginCredentials, multiLoginDestination: string | null): Promise<LoginResponse> {
        let seonPayload = {};
        if (shellLocalConfig.seonConfig.login.isServiceEnabled) {
            const { session, sessionId } = antiFraudService.getCurrentSession();
            // eslint-disable-next-line no-param-reassign

            if (!session || !sessionId) {
                logAntifraudError(AntifraudErrorLocation.LoginGetSession, 'missing_session');
            }

            if (shellLocalConfig.seonConfig?.login.isServiceEnabled) {
                if (antiFraudService.isComtradeSeonFlow()) {
                    seonPayload = {
                        deviceFingerprint: session,
                        externalDeviceId: getDeviceId(),
                    };
                } else {
                    seonPayload = {
                        session,
                        sessionId,
                        deviceFingerprint: getDeviceId(),
                    };
                }
            }
        }

        const wafToken = await wafService.maybeGetToken();

        const headers: LoginHeaders = {
            'Content-Type': 'application/json',
            ...(wafToken ? { 'X-Aws-Waf-Token': wafToken } : {}),
        };

        return this.restClient.post<LoginCredentialsWithSeonSession, LoginResponse>(
            `${this.endpoints.login}${getMultiLoginQuery(multiLoginDestination)}`,
            {
                ...loginCredentials,
                ...seonPayload,
            },
            {
                headers,
            },
        );
    }

    public getSession(): Promise<any> {
        return this.restClient.get(this.endpoints.getSession);
    }

    public checkLoginSession(): Promise<any> {
        return this.restClient.get(this.endpoints.checkLoginSession);
    }

    public logout(): Promise<any> {
        return this.restClient.post(this.endpoints.logout);
    }

    public async activateAccount(token: string, multiLoginDestination: string | null): Promise<any> {
        const { session, sessionId } = antiFraudService.getCurrentSession();

        if (!session || !sessionId) {
            logAntifraudError(AntifraudErrorLocation.ActivateGetSession, 'missing_session');
        }

        const response = await this.restClient.post(
            // TODO remove registerPrefix after replace legacy backend
            `${this.endpoints.apiVersion}/${getConfig().countrySpecific.user.activateAccountRestRoute}${getMultiLoginQuery(multiLoginDestination)}`,
            {
                secureKey: token,
                ...(shellLocalConfig.seonConfig.register.isServiceEnabled && session && sessionId && {
                    session,
                    sessionId,
                }),
            },
            {
                headers: {
                    'Content-Type': 'application/json',
                },
            },
        ) as Promise<CTResponseData<unknown>>;

        if (
            getConfig().countrySpecific.country === Country.Ro
            || getConfig().countrySpecific.country === Country.Pl
        ) {
            return processResponse(response);
        } else {
            if ((response as any).error) {
                throw new Error((response as any).data.ErrorMessage);
            } else {
                return response;
            }
        }
    }

    public updatePassword(oldPassword: string, newPassword: string, otp?: string): Promise<any> {
        return this.restClient.post(
            this.endpoints.updatePassword,
            {
                password: oldPassword,
                newpassword: newPassword,
                repeatpassword: newPassword,
                ...(otp ? { otp } : {}),
            },
            {
                headers: {
                    'Content-Type': 'application/json',
                },
            },
        );
    }

    public forgotPasswordRequest(email: string): Promise<any> {
        return this.restClient.post(
            this.endpoints.forgotPassword,
            {
                emailAddress: email,
            },
            {
                headers: {
                    'Content-Type': 'application/json',
                },
            },
        );
    }

    public sendPlayerLoginNotificationRequest(email: string): Promise<PlayerLoginNotificationResponse> {
        return this.restClient.post(
            this.endpoints.sendPlayerLoginNotification || '#',
            {
                ChannelType: 'EMAIL',
                Value: email,
            },
            {
                headers: {
                    'Content-Type': 'application/json',
                },
            },
        );
    }

    public loginPlayerAlternativeChannelRequest(token: AccountRecoveryToken): Promise<LoginPlayerAlternativeChannelResponse> {
        return this.restClient.post(
            this.endpoints.loginPlayerAlternativeChannel || '#',
            {
                ChannelType: 'EMAIL',
                Value: token,
            },
            {
                headers: {
                    'Content-Type': 'application/json',
                },
            },
        );
    }

    public changeForgottenPassword(token: string, password: string, username: string, hasOtpId: boolean): Promise<any> {
        return this.restClient.post(
            this.endpoints.changeForgottenPassword,
            {
                ...(!hasOtpId && { secureKey: token, username }),
                ...(hasOtpId && { otpId: token, email: username }),
                newPassword: password,
            },
            {
                headers: {
                    'Content-Type': 'application/json',
                },
            },
        );
    }

    public getIdentityProviderUrl(provider: IdentityProvider, redirectUrl?: string): Promise<IdentityProviderUrlResponse> {
        return this.restClient.get(
            this.endpoints.getIdentityProviderUrl!,
            {
                params: {
                    provider,
                    redirect_uri: redirectUrl,
                },
            },
        );
    }

    public getKYCMojeIdResponse(conversationId: string): Promise<any> {
        return this.restClient.get(
            this.endpoints.getKYCMojeIdResponse!,
            {
                params: {
                    conversationId,
                },
            },
        );
    }

    public completeKycReverification(userId: string): Promise<any> {
        const playerId = userId;

        return this.restClient.post(
            this.endpoints.completeKycReverification!,
            {},
            {
                params: {
                    playerId,
                },
            });
    }

    public getKycDetails(): Promise<any> {
        return this.restClient.get(this.endpoints.getKycDetails!);
    }

    public getMfaStatus(params: unknown): Promise<any> {
        return this.restClient.get(
            this.endpoints.getMfaStatus!,
            {
                params,
            },
        );
    }

    public verifyOtpCode(payload: VerifyMfaCodePayload): Promise<VerifyMfaCodeResponse> {
        return this.restClient.post(
            this.endpoints.verifyMfaCode!,
            payload,
        );
    }

    public sendOtpCode(payload: SendMfaCodePayload): Promise<SendMfaCodeResponse> {
        return this.restClient.post(
            this.endpoints.sendMfaCode!,
            payload,
        );
    }

    public getSessionStats(): Promise<SessionStatsResponse> {
        return this.restClient.get(
            this.endpoints.getSessionStats!,
        );
    }
}
